import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Card, Row, Col, Form, Input, DatePicker, Button, Divider, Tooltip } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { MemberProfile } from "../../../../model/members/MemberProfile";
import { MembershipCategory } from "../../../../model/members/MembershipCategory";
import MembershipCategorySelector from "../../MembershipCategorySelector";
import MemberTypeSelector from "../../MemberTypeSelector";
import DirectDebitOverrideMarker from "./DirectDebitOverrideMarker";
import { MemberProfileMode } from "./modeConfig";
import { REQUIRED_PERSONAL_FIELDS, RequiredPersonalField, hasRealDateOfBirth, isPersonalFieldFilled } from "./completion";
import { useMemberService } from "../../../../services/members/MemberServiceProvider";
import { phoneNumberRule } from "../../../../utils/phone";

interface FormValues {
    name: string;
    emailAddress: string;
    phoneNumber: string;
    membershipCategory: string;
    memberType: string;
    validFrom: Dayjs | undefined;
    validTo: Dayjs | undefined;
    dateOfBirth: Dayjs | undefined;
    addressLine1: string;
    addressLine2: string;
    townCity: string;
    countyState: string;
    postZipCode: string;
    emergencyContactName: string;
    emergencyContactPhone: string;
    emergencyContactEmail: string;
}

interface PersonalDetailsFormProps {
    member: MemberProfile;
    mode: MemberProfileMode;
    membershipCategories: MembershipCategory[];
    onUpdate: (values: Partial<MemberProfile>) => void;
    // Create/onboarding entry — swaps the "Save changes" CTA for a single
    // "Quick Save" that creates the member once the required fields are present.
    isCreating?: boolean;
    // Self-service onboarding (vs admin "Create User"). Onboarding requires the
    // full set of personal details (and a DD mandate for recurring plans) before
    // Save enables; admin quick-create keeps the minimal name + email gate.
    selfServiceMode?: boolean;
    // Onboarding only — whether a fully-valid DD mandate has been entered on the
    // Billing tab. Recurring memberships require it before Save; annual/one-off
    // don't. Undefined outside onboarding.
    directDebitComplete?: boolean;
    // Onboarding only — the member ticked "skip direct debit for now". When set,
    // the DD mandate is no longer required to save.
    directDebitSkipped?: boolean;
    // Create only — a member-create request is in flight. Disables the Save button
    // and shows a spinner so a slow create can't be double-submitted.
    creating?: boolean;
    // Create only — the club has configured direct-debit cancellation terms, so a
    // member entering a mandate must tick to accept them before Save.
    directDebitAgreementRequired?: boolean;
    // Create only — whether the member has ticked the cancellation-terms agreement.
    directDebitAgreed?: boolean;
    // Create only — suppress the in-header Save button so the create page can host
    // the Save action(s) elsewhere (top actions bar + bottom of the PARQ card),
    // driving submit through the imperative handle below.
    hideSaveButton?: boolean;
    // Create only — reports Save enablement up so an externally-rendered Save
    // button can mirror the disabled state and missing-fields tooltip.
    onSaveabilityChange?: (state: { canSave: boolean; missingItems: string[] }) => void;
}

// Lets a parent trigger the same member-create submit from a Save button rendered
// outside this card (used by the "Create User" page's top actions bar / PARQ footer).
export interface PersonalDetailsFormHandle {
    submit: () => Promise<void>;
}

// Placeholder text is the absence of an answer, so it must render as an empty input
// rather than as content to keep. Shares isPersonalFieldFilled with the completion
// banner so the form and the banner can never disagree about what's outstanding.
const realOrBlank = (field: RequiredPersonalField["name"], value: string | undefined): string =>
    isPersonalFieldFilled(field, value) ? (value ?? "") : "";

const buildInitialValues = (member: MemberProfile): FormValues => {
    const emergency = member.metadata?.EmergencyContact;
    return {
        name: member.name,
        emailAddress: member.emailAddress,
        // Placeholder text ("To be Completed", an all-zero phone) is not an answer —
        // seed the input EMPTY. Leaving it in place shows the member a filled field
        // directly under a banner saying it's outstanding, and a save from this form
        // writes the placeholder straight back. Same rule the banner itself applies.
        phoneNumber: realOrBlank("phoneNumber", member.phoneNumber),
        membershipCategory: member.membershipCategory?.name ?? "",
        memberType: member.memberType || "Member",
        validFrom: member.validFrom ? dayjs(member.validFrom) : undefined,
        validTo: member.validTo ? dayjs(member.validTo) : undefined,
        // A member with no date of birth on file arrives as a sentinel (the API's
        // DateOfBirth is a non-nullable DateTime), which the picker would otherwise
        // render as a real-looking "31 Dec 0000" — directly contradicting the
        // profile-completion prompt that says it's still needed. Show it as empty.
        dateOfBirth: hasRealDateOfBirth(member.dateOfBirth) ? dayjs(member.dateOfBirth) : undefined,
        addressLine1: realOrBlank("addressLine1", member.addressLine1),
        addressLine2: member.addressLine2,
        townCity: realOrBlank("townCity", member.townCity),
        countyState: member.countyState,
        postZipCode: realOrBlank("postZipCode", member.postZipCode),
        emergencyContactName: emergency?.name || "",
        emergencyContactPhone: emergency?.phoneNumber || "",
        emergencyContactEmail: emergency?.emailAddress || "",
    };
};

// The wide editable details form. Each field's `disabled` is driven by the
// relevant mode flag — a disabled field is still shown (read-only), never
// removed. Reuses the existing PersonalDetails save model: builds a
// Partial<MemberProfile> and calls onUpdate (the screen's handleUpdateMember),
// preserving staff notes (edited separately in the Notes card).
const PersonalDetailsForm = forwardRef<PersonalDetailsFormHandle, PersonalDetailsFormProps>(({ member, mode, membershipCategories, onUpdate, isCreating = false, selfServiceMode = false, directDebitComplete = false, directDebitSkipped = false, creating = false, directDebitAgreementRequired = false, directDebitAgreed = false, hideSaveButton = false, onSaveabilityChange }, ref) => {
    const [form] = Form.useForm<FormValues>();
    const [hasChanges, setHasChanges] = useState(false);
    const memberService = useMemberService();
    const [memberTypes, setMemberTypes] = useState<string[]>([]);

    // Load the global member-type options (Member, Coach) for the assignment
    // dropdown. Loads quietly; a failure just leaves the dropdown with the current
    // value only. Member type is a staff-only concept, so it is not fetched or
    // shown in the self-service flow.
    useEffect(() => {
        if (selfServiceMode) return;
        let active = true;
        memberService
            .getMemberTypes()
            .then((types) => { if (active && Array.isArray(types)) setMemberTypes(types); })
            .catch(() => { /* non-blocking — dropdown falls back to current value */ });
        return () => { active = false; };
    }, [memberService, selfServiceMode]);

    // Self-service onboarding gates Save on the full personal detail set (plus a
    // DD mandate for recurring plans); admin "Create User" keeps the minimal
    // name + email quick-create so staff can create-and-complete-later.
    const isOnboarding = isCreating && selfServiceMode;

    // A member creating their own profile never picks a membership category — the
    // server assigns the "Default New Member" placeholder and staff set the real
    // plan on activation. Staff (admin / front-of-house) creating a member, and
    // everyone in the normal view, still see and edit it.
    const showMembershipCategory = !(isCreating && selfServiceMode);
    // Member type (Member/Coach) is a staff-only access classification — never shown
    // in the self-service flow (a member can't set their own type; the backend also
    // rejects it).
    const showMemberType = !selfServiceMode;
    // A member creating their own profile doesn't set validity dates: the server
    // owns validFrom (today) and derives validTo. Hide them in that flow.
    const showValidityDates = !(isCreating && selfServiceMode);

    // Reactively watch the fields that drive Save enablement so the button (and
    // its missing-fields tooltip) re-evaluate as the member types — including when
    // the DD mandate (an inline card on the create page) changes (directDebitComplete prop).
    const watchedName = Form.useWatch("name", form);
    const watchedEmail = Form.useWatch("emailAddress", form);
    const watchedPhone = Form.useWatch("phoneNumber", form);
    const watchedDob = Form.useWatch("dateOfBirth", form);
    const watchedAddress1 = Form.useWatch("addressLine1", form);
    const watchedTown = Form.useWatch("townCity", form);
    const watchedPostcode = Form.useWatch("postZipCode", form);

    // Keyed by REQUIRED_PERSONAL_FIELDS' field names so the shared required set can
    // be looked up directly. Memoised on the individual watched values (not rebuilt
    // per render) so the missingItems memo below actually memoises.
    const watched = useMemo(
        () => ({
            name: watchedName,
            emailAddress: watchedEmail,
            phoneNumber: watchedPhone,
            dateOfBirth: watchedDob,
            addressLine1: watchedAddress1,
            townCity: watchedTown,
            postZipCode: watchedPostcode,
        }),
        [watchedName, watchedEmail, watchedPhone, watchedDob, watchedAddress1, watchedTown, watchedPostcode],
    );

    // What's still missing before an onboarding/create Save can fire. Drives both
    // the disabled state and the hover tooltip. Empty ⇒ ready to save.
    const missingItems = useMemo(() => {
        if (!isCreating) return [];
        const filled = (v: unknown) => (typeof v === "string" ? v.trim().length > 0 : v != null);

        // A member entering a DD mandate must accept the club's cancellation terms
        // (when configured) before Save — in both the admin and self-service create
        // flows. Skipping DD (self-service opt-out) drops the requirement.
        const directDebitAgreementMissing =
            directDebitComplete && !directDebitSkipped && directDebitAgreementRequired && !directDebitAgreed;
        const AGREEMENT_LABEL = "Agreement to the direct debit cancellation terms";

        if (!isOnboarding) {
            // Admin quick-create: name + email (plus DD agreement if a mandate was entered).
            const missing: string[] = [];
            if (!filled(watched.name)) missing.push("Full name");
            if (!filled(watched.emailAddress)) missing.push("Email");
            if (directDebitAgreementMissing) missing.push(AGREEMENT_LABEL);
            return missing;
        }

        // Self-service onboarding: full personal details (email is prefilled +
        // read-only, so it isn't gated here). The required set is shared with the
        // profile-completion banner/wizard (completion.ts) so the two can't drift —
        // an existing member is judged "incomplete" by exactly the fields a new
        // member has to supply here.
        const missing = REQUIRED_PERSONAL_FIELDS.filter(
            (field) => !isPersonalFieldFilled(field.name, watched[field.name]),
        ).map((field) => field.label);

        // A member creating their own profile doesn't choose a plan (the category
        // field is hidden and the server assigns the default), so we can't tell
        // whether the membership is fee-free — require a direct-debit mandate unless
        // they've explicitly opted to skip it (the club then reviews their membership).
        if (!directDebitComplete && !directDebitSkipped) missing.push("Direct Debit details");
        if (directDebitAgreementMissing) missing.push(AGREEMENT_LABEL);

        return missing;
    }, [isCreating, isOnboarding, watched, directDebitComplete, directDebitSkipped,
        directDebitAgreementRequired, directDebitAgreed]);

    const canQuickSave = missingItems.length === 0;

    // Re-initialise when the member's editable data changes (e.g. a different
    // member, or a server refresh), but NOT when only photoUrl churns — otherwise
    // a late photo load would wipe an in-progress edit.
    const memberSignature = useMemo(() => JSON.stringify({ ...member, photoUrl: undefined }), [member]);

    useEffect(() => {
        form.setFieldsValue(buildInitialValues(member));
        setHasChanges(false);
    }, [memberSignature, form]); // eslint-disable-line react-hooks/exhaustive-deps

    // "Save changes" (view mode) enables when the form differs from the loaded
    // member. Create-flow enablement is derived from missingItems above.
    const handleValuesChange = (_changed: Partial<FormValues>, all: FormValues) => {
        const initial = buildInitialValues(member);
        const changed = (Object.keys(all) as (keyof FormValues)[]).some((key) => {
            if (key === "validFrom" || key === "validTo" || key === "dateOfBirth") {
                const a = all[key] as Dayjs | undefined;
                const b = initial[key] as Dayjs | undefined;
                if (!a && !b) return false;
                if (!a || !b) return true;
                return !a.isSame(b, "day");
            }
            return all[key] !== initial[key];
        });
        setHasChanges(changed);
    };


    const handleSubmit = async () => {
        // On create, enforce phone format like the enrolment forms — new records
        // have no legacy free-text phones to lock out. Edits stay non-blocking so
        // pre-existing non-conforming numbers remain saveable; the inline rule
        // still flags a newly-typed bad value there.
        try {
            // The emergency-contact pair is enforced on BOTH paths: unlike the member's
            // own phone there is no legacy free-text emergency data this would lock out,
            // and a half-entered contact is silently dropped on save and then reported
            // as outstanding forever by the completion prompt.
            const always = ["emergencyContactName", "emergencyContactPhone"];
            await form.validateFields(isCreating ? ["phoneNumber", ...always] : always);
        } catch {
            return; // AntD surfaces the inline field errors.
        }

        const values = form.getFieldsValue();
        // Fall back to the member's existing category when the categories list
        // hasn't loaded (or doesn't contain it) so contact/address-only edits
        // still save instead of silently bailing.
        const selectedCategory =
            membershipCategories.find((c) => c.name === values.membershipCategory) ?? member.membershipCategory;

        const formatted: Partial<MemberProfile> = {
            name: values.name,
            // Username is server-owned and no longer shown; preserve the existing value.
            username: member.username,
            emailAddress: values.emailAddress,
            phoneNumber: values.phoneNumber,
            specialOffers: member.specialOffers,
            dateOfBirth: values.dateOfBirth ? new Date(values.dateOfBirth.format("YYYY-MM-DD")) : undefined,
            addressLine1: values.addressLine1,
            addressLine2: values.addressLine2,
            townCity: values.townCity,
            countyState: values.countyState,
            postZipCode: values.postZipCode,
            metadata: {
                ...member.metadata,
                // Staff notes are edited in their own card — preserve here.
                Notes: member.metadata?.Notes,
                // Explicit null when cleared, NOT an omitted key: the repository only
                // touches metadata keys that are present, and deletes the row when the
                // value is null/blank (MemberRepository.CreateOrUpdate). Omitting it
                // would leave the old contact in place and make it unremovable.
                // Trimmed so whitespace can't masquerade as a contact.
                EmergencyContact: values.emergencyContactName?.trim()
                    ? {
                          name: values.emergencyContactName.trim(),
                          phoneNumber: values.emergencyContactPhone?.trim() || null,
                          emailAddress: values.emergencyContactEmail?.trim() || null,
                      }
                    : null,
            },
            membershipCategory: selectedCategory,
            // Preserve the existing type when the field wasn't shown/edited (e.g.
            // self-service), so a save never silently downgrades a Coach to Member.
            memberType: values.memberType || member.memberType || "Member",
            validFrom: values.validFrom ? new Date(values.validFrom.format("YYYY-MM-DD")) : undefined,
            validTo: values.validTo ? new Date(values.validTo.format("YYYY-MM-DD")) : undefined,
        };

        onUpdate(formatted);
        setHasChanges(false);
    };

    // Expose submit so a Save button rendered outside this card (create page) can
    // fire the same create as the in-header button.
    useImperativeHandle(ref, () => ({ submit: handleSubmit }));

    // Mirror Save enablement up to the create page so its externally-rendered Save
    // button(s) share the disabled state and missing-fields tooltip.
    useEffect(() => {
        if (isCreating) onSaveabilityChange?.({ canSave: canQuickSave, missingItems });
    }, [isCreating, canQuickSave, missingItems, onSaveabilityChange]);

    const sectionLabel = (text: string) => (
        <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ft-muted)" }}>{text}</span>
    );

    const contactDisabled = !mode.editContact;
    // Validity dates fall back to the membership-edit flag unless a mode sets
    // editValidityDates explicitly (onboarding locks dates while unlocking
    // category; adminCreate unlocks both).
    const validityDatesDisabled = !(mode.editValidityDates ?? mode.editMembership);

    return (
        <Card
            title="Personal & contact details"
            size="small"
            extra={
                isCreating && hideSaveButton ? null : isCreating ? (
                    // Tooltip on hover spells out what's still needed. It sits on a
                    // wrapper span because a disabled AntD button doesn't emit the
                    // hover events the tooltip listens for.
                    <Tooltip
                        title={
                            canQuickSave
                                ? undefined
                                : `Complete before saving: ${missingItems.join(", ")}`
                        }
                    >
                        <span data-testid="profile-quick-save-wrap">
                            <Button
                                type="primary"
                                size="small"
                                disabled={!canQuickSave || creating}
                                loading={creating}
                                onClick={handleSubmit}
                                data-testid="profile-quick-save"
                            >
                                Save
                            </Button>
                        </span>
                    </Tooltip>
                ) : (
                    <Button type="primary" size="small" disabled={!hasChanges} onClick={handleSubmit} data-testid="profile-save-changes">
                        Save changes
                    </Button>
                )
            }
        >
            <Form<FormValues> form={form} layout="vertical" requiredMark={false} size="middle" onValuesChange={handleValuesChange}>
                <Row gutter={14}>
                    <Col xs={24}>
                        <Form.Item name="name" label="Full name" style={{ marginBottom: 12 }}>
                            <Input disabled={!mode.editName} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={14}>
                    <Col xs={24} lg={12}>
                        <Form.Item name="emailAddress" label="Email" style={{ marginBottom: 12 }}>
                            <Input type="email" disabled={!mode.editEmail} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                        <Form.Item name="phoneNumber" label="Phone number" rules={[phoneNumberRule()]} style={{ marginBottom: 12 }}>
                            <Input type="tel" disabled={contactDisabled} />
                        </Form.Item>
                    </Col>
                </Row>
                {(showMembershipCategory || showValidityDates || showMemberType) && (
                    <Row gutter={14}>
                        {showMembershipCategory && (
                            <Col xs={24} lg={10}>
                                <Form.Item
                                    name="membershipCategory"
                                    label="Membership category"
                                    style={{ marginBottom: 12 }}
                                    getValueFromEvent={(category) => category?.name ?? ""}
                                >
                                    {/* Form.Item injects value (the category name) + onChange. The
                                        selector's onChange emits a MembershipCategory object, so
                                        getValueFromEvent stores just its name — the field is always a
                                        string (canQuickSave/handleSubmit read the name). Do NOT pass
                                        value/onChange here: Form.Item's cloneElement overrides them,
                                        which previously stored the raw object and broke .trim(). */}
                                    <MembershipCategorySelector
                                        membershipCategories={membershipCategories}
                                        disabled={!mode.editMembership}
                                    />
                                </Form.Item>
                                {/* Price override (custom direct-debit amount) sits directly under
                                    the category so its overridden price reads next to the plan.
                                    Self-hides when there's nothing to show / add. Never shown while
                                    creating: the member doesn't exist yet, and "Add override" here
                                    would route through the create submit and POST a blank member. */}
                                {!isCreating && (
                                    <div style={{ marginTop: -4, marginBottom: 12 }}>
                                        <DirectDebitOverrideMarker
                                            member={member}
                                            canEdit={mode.editDirectDebitOverride}
                                            onSave={(amount) =>
                                                onUpdate({ metadata: { ...member.metadata, DirectDebitOverrideAmount: amount } })
                                            }
                                        />
                                    </div>
                                )}
                            </Col>
                        )}
                        {showMemberType && (
                            <Col xs={24} lg={7}>
                                <Form.Item
                                    name="memberType"
                                    label="Member type"
                                    style={{ marginBottom: 12 }}
                                >
                                    {/* Form.Item injects value + onChange. The selector emits the
                                        selected type name (a string) directly, so no
                                        getValueFromEvent is needed (unlike the category selector). */}
                                    <MemberTypeSelector
                                        memberTypes={memberTypes.length > 0 ? memberTypes : [member.memberType || "Member"]}
                                        disabled={!mode.editMembership}
                                    />
                                </Form.Item>
                            </Col>
                        )}
                        {showValidityDates && (
                            <Col xs={12} lg={7}>
                                <Form.Item name="validFrom" label="Valid from" style={{ marginBottom: 12 }}>
                                    <DatePicker style={{ width: "100%" }} format="DD MMM YYYY" disabled={validityDatesDisabled} allowClear={false} />
                                </Form.Item>
                            </Col>
                        )}
                        {showValidityDates && (
                            <Col xs={12} lg={7}>
                                <Form.Item name="validTo" label="Valid to" style={{ marginBottom: 12 }}>
                                    <DatePicker style={{ width: "100%" }} format="DD MMM YYYY" disabled={validityDatesDisabled} allowClear={false} />
                                </Form.Item>
                            </Col>
                        )}
                    </Row>
                )}

                <Divider style={{ margin: "6px 0 14px" }} orientation="left" orientationMargin={0}>
                    {sectionLabel("Address & personal")}
                </Divider>
                <Row gutter={14}>
                    <Col xs={24} lg={8}>
                        <Form.Item name="dateOfBirth" label="Date of birth" style={{ marginBottom: 12 }}>
                            <DatePicker style={{ width: "100%" }} format="DD MMM YYYY" disabled={contactDisabled} allowClear={false} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} lg={16}>
                        <Form.Item name="addressLine1" label="Address line 1" style={{ marginBottom: 12 }}>
                            <Input disabled={contactDisabled} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={14}>
                    <Col xs={24} lg={24}>
                        <Form.Item name="addressLine2" label="Address line 2" style={{ marginBottom: 12 }}>
                            <Input disabled={contactDisabled} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={14}>
                    <Col xs={24} lg={8}>
                        <Form.Item name="townCity" label="Town / City" style={{ marginBottom: 12 }}>
                            <Input disabled={contactDisabled} />
                        </Form.Item>
                    </Col>
                    <Col xs={12} lg={8}>
                        <Form.Item name="countyState" label="County" style={{ marginBottom: 12 }}>
                            <Input disabled={contactDisabled} />
                        </Form.Item>
                    </Col>
                    <Col xs={12} lg={8}>
                        <Form.Item name="postZipCode" label="Postcode" style={{ marginBottom: 12 }}>
                            <Input disabled={contactDisabled} />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider style={{ margin: "6px 0 14px" }} orientation="left" orientationMargin={0}>
                    {sectionLabel("Emergency contact")}
                </Divider>
                <Row gutter={14}>
                    <Col xs={24} lg={8}>
                        {/* The name is what carries the contact — a phone or email with
                            nobody attached is dropped on save, so demand a name once
                            either is filled in. */}
                        <Form.Item
                            name="emergencyContactName"
                            label="Name"
                            dependencies={["emergencyContactPhone", "emergencyContactEmail"]}
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator: (_, value) => {
                                        const anyContact =
                                            !!getFieldValue("emergencyContactPhone")?.trim() ||
                                            !!getFieldValue("emergencyContactEmail")?.trim();
                                        if (!anyContact || value?.trim()) return Promise.resolve();
                                        return Promise.reject(new Error("Add a name for your emergency contact"));
                                    },
                                }),
                            ]}
                            style={{ marginBottom: 0 }}
                        >
                            <Input disabled={contactDisabled} />
                        </Form.Item>
                    </Col>
                    <Col xs={12} lg={8}>
                        {/* A named contact with no phone and no email can't be reached,
                            so it doesn't satisfy hasEmergencyContact and the completion
                            prompt would keep asking for it. Require one or the other
                            once a name is given — same rule as the completion wizard. */}
                        <Form.Item
                            name="emergencyContactPhone"
                            label="Phone"
                            dependencies={["emergencyContactName", "emergencyContactEmail"]}
                            rules={[
                                phoneNumberRule(),
                                ({ getFieldValue }) => ({
                                    validator: (_, value) => {
                                        const named = !!getFieldValue("emergencyContactName")?.trim();
                                        const hasEmail = !!getFieldValue("emergencyContactEmail")?.trim();
                                        if (!named || hasEmail || value?.trim()) return Promise.resolve();
                                        return Promise.reject(
                                            new Error("Add a phone or an email for your emergency contact"),
                                        );
                                    },
                                }),
                            ]}
                            style={{ marginBottom: 0 }}
                        >
                            <Input type="tel" disabled={contactDisabled} />
                        </Form.Item>
                    </Col>
                    <Col xs={12} lg={8}>
                        <Form.Item name="emergencyContactEmail" label="Email" style={{ marginBottom: 0 }}>
                            <Input disabled={contactDisabled} />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Card>
    );
});

PersonalDetailsForm.displayName = "PersonalDetailsForm";

export default PersonalDetailsForm;
